import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { alert } from 'actions/flash'
import {
  useGenerateSuggestionsMutation,
  useUpdateOrderingErrorConfigMutation,
} from 'api/orderingErrorConfig'
import { buildTranslate } from 'locales'
import {
  acceptAiSuggestion,
  acceptAllAiSuggestions,
  addGeneratingCategoryId,
  applyAiSuggestions,
  closeAiModal,
  initializeCustomizations,
  openAiModal,
  publishSuccess,
  rejectAiSuggestion,
  rejectAllAiSuggestions,
  removeGeneratingCategoryId,
  revertCustomization,
  selectCustomization,
  selectCustomizationSource,
  selectHasChanges,
  selectHasPendingAiSuggestions,
  selectInitialCustomization,
  selectIsGeneratingCategory,
  selectOrderingErrorMessages,
  selectPendingAiSuggestion,
  selectPendingSingleCategoryId,
  selectPendingSuggestionsCount,
  setBrandTone,
  setCustomization,
  setLastUsedBrandTone,
  setPendingAiSuggestions,
  setPendingSingleCategoryId,
  snapshotForAi,
} from 'slices/orderingErrorMessages'
import { DEFAULT_BRAND_TONE } from 'slices/orderingErrorMessages/types'

const t = buildTranslate('ordering_management.error_messages')

export function useOrderingErrorMessages() {
  const dispatch = useDispatch()
  const state = useSelector(selectOrderingErrorMessages)
  const hasChanges = useSelector(selectHasChanges)
  const hasPendingSuggestions = useSelector(selectHasPendingAiSuggestions)
  const pendingSuggestionsCount = useSelector(selectPendingSuggestionsCount)
  const pendingSingleCategoryId = useSelector(selectPendingSingleCategoryId)

  const [updateConfig, { isLoading: isSaving }] =
    useUpdateOrderingErrorConfigMutation()
  const [generateSuggestions, { isLoading: isGenerating }] =
    useGenerateSuggestionsMutation()
  const [isSavingTone, setIsSavingTone] = useState(false)

  const initialize = useCallback(
    (
      categories: Array<{
        id: string
        custom_message: string | null
        source?: 'ai' | 'manual' | null
      }>,
      brandTone?: string | null
    ) => {
      dispatch(initializeCustomizations({ categories, brandTone }))
    },
    [dispatch]
  )

  const openAiModalAction = useCallback(() => {
    dispatch(openAiModal())
  }, [dispatch])

  const closeAiModalAction = useCallback(() => {
    dispatch(closeAiModal())
  }, [dispatch])

  const updateBrandTone = useCallback(
    (tone: string) => {
      dispatch(setBrandTone(tone))
    },
    [dispatch]
  )

  const revert = useCallback(
    (categoryId: string) => {
      dispatch(revertCustomization(categoryId))
    },
    [dispatch]
  )

  const acceptSuggestion = useCallback(
    (categoryId: string) => {
      dispatch(acceptAiSuggestion(categoryId))
    },
    [dispatch]
  )

  const rejectSuggestion = useCallback(
    (categoryId: string) => {
      dispatch(rejectAiSuggestion(categoryId))
    },
    [dispatch]
  )

  const acceptAllSuggestions = useCallback(() => {
    dispatch(acceptAllAiSuggestions())
  }, [dispatch])

  const rejectAllSuggestions = useCallback(() => {
    dispatch(rejectAllAiSuggestions())
  }, [dispatch])

  const buildPublishPayload = useCallback(() => {
    const customizations: Record<
      string,
      { custom_message: string | null; source?: 'ai' | 'manual' | null }
    > = {}
    Object.entries(state.customizations).forEach(([id, message]) => {
      if (message !== state.initialCustomizations[id]) {
        const entry: {
          custom_message: string | null
          source?: 'ai' | 'manual' | null
        } = { custom_message: message || null }
        if (message) {
          entry.source = state.customizationSources[id] || 'manual'
        }
        customizations[id] = entry
      }
    })

    if (Object.keys(customizations).length === 0) return null

    const payload: {
      customizations: typeof customizations
      brand_tone?: string
    } = { customizations }

    if (state.lastUsedBrandTone) {
      payload.brand_tone = state.lastUsedBrandTone
    }

    return payload
  }, [
    state.customizations,
    state.initialCustomizations,
    state.customizationSources,
    state.lastUsedBrandTone,
  ])

  const handleSaveTone = useCallback(
    async (brandTone: string) => {
      setIsSavingTone(true)
      try {
        await updateConfig({
          customizations: {},
          brand_tone: brandTone,
        }).unwrap()
        dispatch(setLastUsedBrandTone(brandTone))
        dispatch(
          alert({
            key: 'success',
            message: t('brand_tone_saved'),
            timeout: 5,
          })
        )
      } catch (error) {
        dispatch(
          alert({
            key: 'danger',
            message: t('brand_tone_save_failed'),
          })
        )
      } finally {
        setIsSavingTone(false)
      }
    },
    [dispatch, updateConfig]
  )

  const handleGenerateAi = useCallback(
    async (brandTone: string) => {
      try {
        dispatch(snapshotForAi())

        const result = await generateSuggestions({
          brand_tone: brandTone,
        }).unwrap()

        const singleCategoryId = pendingSingleCategoryId
        dispatch(setPendingSingleCategoryId(null))

        if (singleCategoryId) {
          const match = result.suggestions.filter(
            (s: { category_id: string }) => s.category_id === singleCategoryId
          )
          dispatch(setPendingAiSuggestions(match))
        } else {
          const uncustomized = result.suggestions.filter(
            (s: { category_id: string }) => !state.customizations[s.category_id]
          )
          dispatch(setPendingAiSuggestions(uncustomized))
        }

        dispatch(setLastUsedBrandTone(brandTone))
        dispatch(closeAiModal())

        dispatch(
          alert({
            key: 'success',
            message: t('ai_generation_success'),
            timeout: 5,
          })
        )
      } catch (error) {
        dispatch(
          alert({
            key: 'danger',
            message: t('ai_generation_error'),
          })
        )
      }
    },
    [
      dispatch,
      generateSuggestions,
      state.customizations,
      pendingSingleCategoryId,
    ]
  )

  const handlePerEntryAi = useCallback(
    async (categoryId: string) => {
      const tone = state.lastUsedBrandTone || DEFAULT_BRAND_TONE

      dispatch(snapshotForAi())
      dispatch(addGeneratingCategoryId(categoryId))
      try {
        const result = await generateSuggestions({
          brand_tone: tone,
        }).unwrap()

        const match = result.suggestions.find(
          (s: { category_id: string }) => s.category_id === categoryId
        )
        if (match) {
          dispatch(applyAiSuggestions([match]))
        } else {
          dispatch(
            alert({
              key: 'warning',
              message: t('ai_no_suggestions_for_category'),
            })
          )
        }
        dispatch(setLastUsedBrandTone(tone))
      } catch (error) {
        dispatch(
          alert({
            key: 'danger',
            message: t('ai_generation_error'),
          })
        )
      } finally {
        dispatch(removeGeneratingCategoryId(categoryId))
      }
    },
    [state.lastUsedBrandTone, dispatch, generateSuggestions]
  )

  const handlePublish = useCallback(async () => {
    const payload = buildPublishPayload()

    if (!payload) {
      dispatch(
        alert({
          key: 'info',
          message: t('no_changes'),
          timeout: 3,
        })
      )
      return
    }

    try {
      await updateConfig(payload).unwrap()
      dispatch(publishSuccess())
      dispatch(
        alert({
          key: 'success',
          message: t('publish_success'),
          timeout: 5,
        })
      )
    } catch (error) {
      dispatch(
        alert({
          key: 'danger',
          message: t('publish_error'),
        })
      )
    }
  }, [dispatch, updateConfig, buildPublishPayload])

  return {
    hasChanges,
    hasPendingSuggestions,
    pendingSuggestionsCount,
    aiModal: state.aiModal,
    lastUsedBrandTone: state.lastUsedBrandTone,
    initialize,
    openAiModal: openAiModalAction,
    closeAiModal: closeAiModalAction,
    updateBrandTone,
    revert,
    acceptSuggestion,
    rejectSuggestion,
    acceptAllSuggestions,
    rejectAllSuggestions,
    handleSaveTone,
    handleGenerateAi,
    handlePerEntryAi,
    handlePublish,
    isSavingTone,
    isGenerating,
    isSaving,
  }
}

export function useErrorCategoryEditing(categoryId: string) {
  const dispatch = useDispatch()
  const customMessage = useSelector(selectCustomization(categoryId))
  const initialMessage = useSelector(selectInitialCustomization(categoryId))
  const source = useSelector(selectCustomizationSource(categoryId))
  const pendingAiSuggestion = useSelector(selectPendingAiSuggestion(categoryId))
  const isGeneratingAi = useSelector(selectIsGeneratingCategory(categoryId))

  const hasChanges = customMessage !== initialMessage

  const updateMessage = useCallback(
    (value: string) => {
      dispatch(setCustomization({ categoryId, message: value || null }))
    },
    [dispatch, categoryId]
  )

  const undoToInitial = useCallback(() => {
    dispatch(setCustomization({ categoryId, message: initialMessage }))
  }, [dispatch, categoryId, initialMessage])

  const revert = useCallback(() => {
    dispatch(revertCustomization(categoryId))
  }, [dispatch, categoryId])

  const acceptSuggestion = useCallback(() => {
    dispatch(acceptAiSuggestion(categoryId))
  }, [dispatch, categoryId])

  const rejectSuggestion = useCallback(() => {
    dispatch(rejectAiSuggestion(categoryId))
  }, [dispatch, categoryId])

  return {
    customMessage,
    hasChanges,
    isGeneratingAi,
    pendingAiSuggestion,
    source,
    updateMessage,
    undoToInitial,
    revert,
    acceptSuggestion,
    rejectSuggestion,
  }
}
